import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

/**
 * AddressAutocomplete Component
 * 
 * Reusable address autocomplete with Google Places / Mapbox / SmartyStreets support
 * 
 * Usage:
 * ```jsx
 * <AddressAutocomplete
 *   onAddressSelect={(address) => {
 *     setFormData({
 *       ...formData,
 *       street_line_1: address.street_line_1,
 *       street_line_2: address.street_line_2,
 *       city: address.city,
 *       state: address.state,
 *       postal_code: address.postal_code,
 *       country: address.country
 *     });
 *   }}
 *   initialValue={formData.street_line_1}
 *   placeholder="Start typing an address..."
 * />
 * ```
 */
function AddressAutocomplete({ 
  onAddressSelect,
  // Called on every keystroke with the raw text, for callers that must
  // keep a required field in step whether or not a suggestion is ever
  // picked. Optional: existing callers are unaffected.
  onInputChange,

  initialValue = '', 
  placeholder = 'Start typing an address...',
  country = 'US',
  disabled = false,
  // Both inputs below rendered with no class and no styling, so the address
  // box sat visibly smaller than the fields around it. The caller supplies the
  // class, because there is no app-wide input class to default to — the one
  // used by the patient wizard is defined inside that file's own <style> block,
  // so defaulting to it here would style one screen and quietly do nothing on
  // the other nine that use this component.
  className = '',
  inputStyle = {},
}) {
  /* A DEFAULT THAT LOOKS LIKE AN INPUT.
     inputStyle defaulted to {} — an input with no padding, no height and no
     border, sitting visibly smaller than every field beside it. Twelve
     screens call this component without passing a style, so twelve screens
     had an address box that did not match the form around it.

     Styling it here fixes all of them at once, and anything a caller passes
     still wins because it is spread afterwards. Sized and coloured from the
     same CSS variables the rest of the app uses, so it follows the theme
     rather than pinning a colour that is wrong in one of them. */
  const boxStyle = {
    minHeight: 42,
    padding: '0 12px',
    fontSize: 14,
    borderRadius: 9,
    border: '1px solid var(--border-color, rgba(148,163,184,.3))',
    background: 'transparent',
    color: 'var(--text-primary, inherit)',
    boxSizing: 'border-box',
    ...inputStyle,
  };
  const [query, setQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [providerAvailable, setProviderAvailable] = useState(false);
  const debounceTimer = useRef(null);
  const containerRef = useRef(null);
  // Counter for search requests. Only the newest one is allowed to touch the
  // list — see searchAddresses and handleSuggestionClick.
  const searchSeq = useRef(0);

  const API_BASE = process.env.REACT_APP_API_URL || (window.location.protocol === 'https:' ? `${window.location.protocol}//${window.location.host}` : `http://${window.location.hostname}:8000`);

  // Check if address autocomplete provider is configured
  useEffect(() => {
    checkProviderStatus();
  }, []);

  // Update query when initialValue changes
  useEffect(() => {
    setQuery(initialValue);
  }, [initialValue]);

  // Close suggestions when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const checkProviderStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/address-autocomplete/status`);
      setProviderAvailable(response.data.available);
    } catch (err) {
      console.warn('Address autocomplete not configured:', err);
      setProviderAvailable(false);
    }
  };

  const searchAddresses = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }

    const seq = ++searchSeq.current;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/address-autocomplete/search`, {
        params: { query: searchQuery, country }
      });
      // A search started before the user picked an address must not reopen the
      // list when it finally lands. Typing stops, the debounce fires, the user
      // clicks a suggestion a moment later — and this response arrives after
      // the click and puts the dropdown straight back over the chosen address.
      if (seq !== searchSeq.current) return;
      setSuggestions(response.data);
      setShowSuggestions(true);
    } catch (err) {
      console.error('Address search error:', err);
      if (seq === searchSeq.current) setSuggestions([]);
    } finally {
      if (seq === searchSeq.current) setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Debounce API calls
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchAddresses(value);
    }, 300);
  };

  const handleSuggestionClick = async (suggestion) => {
    // Shut the search pipeline down before anything else. A debounce still
    // counting down, or a request already in flight, both end in
    // setShowSuggestions(true) — after this handler has closed the list.
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    searchSeq.current++;               // invalidates any in-flight search

    setQuery(suggestion.main_text);
    // Emptied, not merely hidden. handleFocus reopens the list whenever there
    // are suggestions left in state, so a stale list would spring back the
    // next time the field took focus.
    setSuggestions([]);
    setShowSuggestions(false);
    setLoading(true);

    try {
      const response = await axios.get(`${API_BASE}/api/address-autocomplete/details`, {
        params: { place_id: suggestion.place_id }
      });
      
      // Call parent callback with full address details
      onAddressSelect(response.data);
    } catch (err) {
      console.error('Address details error:', err);
      alert('Failed to get address details');
    } finally {
      setLoading(false);
    }
  };

  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // If provider not available, render standard input
  if (!providerAvailable) {
    return (
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); onInputChange?.(e.target.value); }}
        onBlur={(e) => onAddressSelect({ street_line_1: e.target.value })}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        style={{ width: '100%', ...boxStyle }}
      />
    );
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <input
        type="text"
        value={query}
        onChange={(e) => { handleInputChange(e); onInputChange?.(e.target.value); }}
        onFocus={handleFocus}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
        style={{ width: '100%', ...boxStyle }}
        autoComplete="off"
      />
      
      {loading && (
        <div style={{
          position: 'absolute',
          right: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '12px',
          color: '#666'
        }}>
          Loading...
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          background:  'var(--surface-elevated, #ffffff)',
          color:       'var(--text-primary, #111827)',
          border:      '1px solid var(--divider, #e5e7eb)',
          borderRadius: 8,
          marginTop: 4,
          maxHeight: 300,
          overflowY: 'auto',
          boxShadow: '0 10px 28px rgba(0, 0, 0, 0.35)',
          zIndex: 1000,
        }}>
          {suggestions.map((suggestion, idx) => (
            <div
              key={idx}
              onClick={() => handleSuggestionClick(suggestion)}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                borderBottom: idx < suggestions.length - 1
                  ? '1px solid var(--divider, #eee)'
                  : 'none',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(59,130,246,0.12)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div style={{ fontWeight: 600, marginBottom: 2,
                             color: 'var(--text-primary, #111827)' }}>
                {suggestion.main_text}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>
                {suggestion.secondary_text}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AddressAutocomplete;
